import { useRef, useState } from "react";
import { uploadToStorage } from "../lib/uploads";
import { Upload, Loader2, Image as ImageIcon, X } from "lucide-react";

/**
 * Tap-to-upload image picker — uploads to Emergent Object Store.
 */
export default function ImageUploader({ value, onChange, testId = "img-upload", aspect = "16/9", label = "Cover Image" }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { setErr("Please pick an image"); return; }
    if (file.size > 10 * 1024 * 1024) { setErr("Image must be under 10MB"); return; }
    setErr(""); setBusy(true);
    try {
      const data = await uploadToStorage(file, "images");
      onChange?.(data.secure_url);
    } catch (e) {
      setErr(e?.message || "Upload failed");
    } finally { setBusy(false); }
  };

  const pick = (e) => upload(e.target.files?.[0]);
  const reset = () => { onChange?.(""); if (fileRef.current) fileRef.current.value = ""; };

  return (
    <div data-testid={testId} className="space-y-1.5">
      <label className="text-[10px] uppercase tracking-wider text-white/50 font-bold">{label}</label>
      {value ? (
        <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-black/30" style={{ aspectRatio: aspect }}>
          <img src={value} alt="" className="w-full h-full object-cover"/>
          <button type="button" onClick={reset} data-testid={`${testId}-clear`}
            className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 backdrop-blur flex items-center justify-center active:scale-90">
            <X className="w-4 h-4 text-white"/>
          </button>
          <label className="absolute bottom-2 right-2 px-2.5 py-1 rounded-full bg-[#FF7A00] text-black text-[10px] font-extrabold flex items-center gap-1 cursor-pointer active:scale-95">
            <Upload className="w-3 h-3"/>Replace
            <input ref={fileRef} type="file" accept="image/*" onChange={pick} className="hidden"/>
          </label>
        </div>
      ) : (
        <label className="flex flex-col items-center justify-center gap-1.5 w-full py-6 rounded-2xl border-2 border-dashed border-white/15 bg-white/5 cursor-pointer hover:border-[#00E5FF]/60 transition" data-testid={`${testId}-dropzone`}>
          {busy ? <Loader2 className="w-6 h-6 text-[#00E5FF] animate-spin"/> : <ImageIcon className="w-6 h-6 text-white/50"/>}
          <span className="text-xs text-white/70">{busy ? "Uploading…" : "Tap to pick image from gallery"}</span>
          <span className="text-[10px] text-white/40">jpg, png, webp · up to 10MB</span>
          <input ref={fileRef} type="file" accept="image/*" onChange={pick} className="hidden" data-testid={`${testId}-input`}/>
        </label>
      )}
      {err && <div className="text-[#FF2A5F] text-xs" data-testid={`${testId}-err`}>{err}</div>}
    </div>
  );
}
